import { Temporal } from "temporal-polyfill";
import { HorizonError, SCALES } from "./types.js";
const PATTERNS = {
    year: /^(\d{4})$/,
    month: /^(\d{4})-(\d{2})$/,
    week: /^(\d{4})-W(\d{2})$/,
    day: /^(\d{4})-(\d{2})-(\d{2})$/,
};
const pad = (n, w = 2) => String(n).padStart(w, "0");
/** Розбирає ключ. Повертає `null` на будь-чому, що не є канонічним ключем. */
export function parseHorizonKey(key) {
    for (const kind of SCALES) {
        const m = PATTERNS[kind].exec(key);
        if (!m)
            continue;
        // Форма збіглася — лишається перевірити, що така дата існує.
        try {
            anchorOf({ kind, key });
        }
        catch {
            return null;
        }
        return { kind, key };
    }
    return null;
}
/**
 * Перший день горизонту. Для тижня це понеділок — так надруковано
 * в паперовому планері, і так вимагає ISO-8601.
 */
export function anchorOf(h) {
    const m = PATTERNS[h.kind].exec(h.key);
    if (!m)
        throw new HorizonError(`ключ «${h.key}» не є ключем ${h.kind}`);
    switch (h.kind) {
        // overflow reject обовʼязковий: за замовчуванням Temporal *затискає*
        // неіснуючу дату до межі місяця, і 2026-02-30 мовчки стало б 28 лютого.
        case "year":
            return Temporal.PlainDate.from({ year: Number(m[1]), month: 1, day: 1 }, { overflow: "reject" });
        case "month":
            return Temporal.PlainDate.from({ year: Number(m[1]), month: Number(m[2]), day: 1 }, { overflow: "reject" });
        case "day":
            return Temporal.PlainDate.from({ year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) }, { overflow: "reject" });
        case "week":
            return mondayOfIsoWeek(Number(m[1]), Number(m[2]));
    }
}
/**
 * Понеділок ISO-тижня. Рік тут — **рік тижня**, а не календарний:
 * 2026-W53 може починатися в грудні 2026 і закінчуватися в січні 2027.
 */
function mondayOfIsoWeek(isoYear, week) {
    if (week < 1 || week > 53)
        throw new HorizonError(`тижня ${week} не існує`);
    // 4 січня завжди лежить у першому ISO-тижні — це визначення стандарту.
    const jan4 = Temporal.PlainDate.from({ year: isoYear, month: 1, day: 4 }, { overflow: "reject" });
    const firstMonday = jan4.subtract({ days: jan4.dayOfWeek - 1 });
    const monday = firstMonday.add({ weeks: week - 1 });
    if (monday.yearOfWeek !== isoYear) {
        throw new HorizonError(`у ${isoYear} році немає ${week}-го тижня`);
    }
    return monday;
}
/** Ключ горизонту, у якому лежить дата. */
export function keyOf(kind, date) {
    switch (kind) {
        case "year":
            return String(date.year);
        case "month":
            return `${date.year}-${pad(date.month)}`;
        case "day":
            return `${date.year}-${pad(date.month)}-${pad(date.day)}`;
        case "week":
            // yearOfWeek, не year: інакше 1 січня 2027 потрапило б у 2027-W53.
            return `${date.yearOfWeek}-W${pad(date.weekOfYear ?? 0)}`;
    }
}
export function horizonOf(kind, date) {
    return { kind, key: keyOf(kind, date) };
}
/** Останній день горизонту. */
export function endOf(h) {
    const a = anchorOf(h);
    switch (h.kind) {
        case "year":
            return a.add({ years: 1 }).subtract({ days: 1 });
        case "month":
            return a.add({ months: 1 }).subtract({ days: 1 });
        case "week":
            return a.add({ days: 6 });
        case "day":
            return a;
    }
}
/** Сусідній період того самого масштабу. Відʼємне `delta` — назад. */
export function shift(h, delta) {
    const a = anchorOf(h);
    const unit = { year: "years", month: "months", week: "weeks", day: "days" };
    return horizonOf(h.kind, a.add({ [unit[h.kind]]: delta }));
}
/**
 * Зміна масштабу зі збереженням прив'язки — це і є навігація в Beresta.
 * Правило одне в обидва боки: горизонт нового масштабу, який **містить
 * перший день** поточного.
 *
 * Наслідок, який виглядає дивно, але правильний: тиждень, отриманий
 * із серпня, може починатися в липні. Так само поводиться й папір —
 * перший рядок сітки місяця містить кілька днів попереднього.
 */
export function zoomTo(h, kind) {
    return kind === h.kind ? h : horizonOf(kind, anchorOf(h));
}
export function parentOf(h) {
    const i = SCALES.indexOf(h.kind);
    return i <= 0 ? null : zoomTo(h, SCALES[i - 1]);
}
export function contains(h, date) {
    return (Temporal.PlainDate.compare(date, anchorOf(h)) >= 0 &&
        Temporal.PlainDate.compare(date, endOf(h)) <= 0);
}
/** Усі дні горизонту. Для року це 365 значень — викликати свідомо. */
export function daysOf(h) {
    const out = [];
    const last = endOf(h);
    for (let d = anchorOf(h); Temporal.PlainDate.compare(d, last) <= 0; d = d.add({ days: 1 })) {
        out.push(d);
    }
    return out;
}
/** Тижні, які перетинає місяць — рядки сітки. Їх буває 4, 5 або 6. */
export function weeksOf(month) {
    if (month.kind !== "month")
        throw new HorizonError("тижні беруться лише з місяця");
    const out = [];
    const last = endOf(month);
    let w = horizonOf("week", anchorOf(month));
    while (Temporal.PlainDate.compare(anchorOf(w), last) <= 0) {
        out.push(w);
        w = shift(w, 1);
    }
    return out;
}
/**
 * Сьогодні у вказаній зоні. Зона потрібна лише тут — далі домен її не знає.
 * Це єдина точка, де календарний день залежить від того, де стоїть пристрій.
 */
export function today(timeZone = Temporal.Now.timeZoneId()) {
    return Temporal.Now.plainDateISO(timeZone);
}
//# sourceMappingURL=horizon.js.map